import React, { useContext } from 'react';
import PropTypes from 'prop-types';
import className from 'classnames';
import { useSelector } from 'react-redux';
import { Icon } from '../../../components';
import { SlideGroupContext } from '../../../components/SlideManager';
import { ABUtils, Config, TrackingUtils } from '../../../utils';
import { EntryPoint } from '../../../models';
import { isCurrentSearchPopulated } from '../../../redux/currentSearch/selectors';

// eslint-disable-next-line complexity
const Field = ({ errorMessage, ErrorMessageComponent, id, icon, placeholder, value, isDisabled }) => {
    const context = useContext(SlideGroupContext);
    const hasCurrentSearchPopulated = useSelector((state) => isCurrentSearchPopulated(state));
    const { isCssVariationMobile, isOneEntryPoint, isOneEntryPointAppBanner } = ABUtils.getABs();
    const brand = Config.get('brand');
    const direction = Config.get('isRtl') ? 'rtl' : 'ltr';
    const currentEntryPoint = Config.get('entryPoint');
    const isOneEntryPointEnabled = Config.get('isOneEntryPointEnabled');
    const showOneEntryPoint = isOneEntryPointEnabled && !hasCurrentSearchPopulated;
    const iconStyle = direction === 'rtl' ? { left: '-16px' } : { right: '-16px' };
    const invalidClassName = className({
        'invalid': !!errorMessage && !isCssVariationMobile,
    });
    const iconClassName = className({
        'odf-icon-2xl odf-input-icon odf-input-inline-icon odf-clickable': !isCssVariationMobile,
        'odf-icon-left odf-text-mono-color-06 odf-space-inner-left-xs': !isCssVariationMobile,
        'prisma-icon-group-left': isCssVariationMobile && !showOneEntryPoint,
        'odf-icon-xl odf-bold odf-text-primary': showOneEntryPoint,
        'odf-icon-bidi': true,
    });
    const inputGroupClassName = className({
        'odf-input-group odf-input-group-left odf-space-outer-top-s': !isCssVariationMobile,
        'prisma-input-group': isCssVariationMobile,
        'odf-space-outer-bottom-s': !errorMessage && isCssVariationMobile && !showOneEntryPoint,
        'invalid odf-space-outer-bottom-l': !!errorMessage && isCssVariationMobile,
    });
    const inputClassName = className({
        'odf-input-l odf-text-lg odf-input odf-clickable odf-text-ellipsis': !isCssVariationMobile,
        'prisma-input': isCssVariationMobile,
        'od-input-one-entry': isOneEntryPoint && showOneEntryPoint,
        'od-input-one-entry-iteration2': isOneEntryPointAppBanner && showOneEntryPoint,
    });

    const inputLabelSpanStyle = isOneEntryPoint ? {
        textAlign: 'center',
        paddingTop: '24px'
    } : {
        paddingTop: '26px'
    };

    const arrowIconClassName = className({
        'odf-icon odf-icon-arrow-right-tail': true,
        'odf-text-neutral-800"': brand === 'ED' || brand === 'GV',
        'odf-text-mono-color-09': brand === 'OP' || brand === 'TR',
    });
    const onFieldClick = () => {
        if (!isDisabled) {
            context.open(id);
            const label = TrackingUtils.LABEL.formFieldClicked(id, showOneEntryPoint);
            if (label !== '' && (currentEntryPoint === EntryPoint.HOME || currentEntryPoint === EntryPoint.FLIGHTS)) {
                TrackingUtils.trackEvent({
                    action: TrackingUtils.ACTION.FLIGHTS,
                    label: label,
                });
            }
        }
    };

    return (
        <div className={invalidClassName}>
            <div
                className={inputGroupClassName}
                onClick={onFieldClick}
            >
                <input
                    type="text"
                    id={id}
                    data-testid={id}
                    className={inputClassName}
                    value={value}
                    readOnly
                    placeholder={placeholder}
                    disabled={isDisabled}
                    style={{ ...(isDisabled ? { color: '#AAAAAA' } : {}) }}
                />
                {!isCssVariationMobile && (
                    <span>
                        <Icon
                            style={{ zIndex: 0 }}
                            glyph={icon}
                            className={iconClassName}
                        />
                    </span>
                )}
                {isCssVariationMobile && (
                    <>
                        <label className="prisma-label prisma-label-valid">
                            {!showOneEntryPoint && (
                                <span className="prisma-inner-label">{placeholder}</span>
                            )}
                            {showOneEntryPoint && (
                                <span
                                    className="prisma-inner-label odf-text-neutral-800 odf-text-light"
                                    style={inputLabelSpanStyle}
                                >
                                    <Icon
                                        style={{ zIndex: 0, paddingRight: '8px', verticalAlign: 'top' }}
                                        glyph={icon}
                                        className={iconClassName}
                                    />
                                    {placeholder}
                                </span>
                            )}
                        </label>
                        {!!errorMessage && (
                            <>
                                <div className="prisma-form-icons" style={iconStyle}>
                                    <span className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-clear
                                        odf-icon odf-icon-cross-circle-light"
                                    />
                                    <span className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-valid
                                        odf-icon odf-icon-tick-circle"
                                    />
                                    <span className="odf-space-outer-right-m prisma-input-icon prisma-input-icon-error
                                        odf-icon odf-icon-exclamation-circle"
                                    />
                                </div>
                                {ErrorMessageComponent ? (
                                    <ErrorMessageComponent errorMessage={errorMessage} dataTestId={id} />
                                ) : (
                                    <div className="prisma-labels odf-text-sm" data-testid="room-selector-children-error">
                                        <div className="odf-row-fluid odf-strong prisma-label-error">
                                            {errorMessage}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </>
                )}
            </div>
            {showOneEntryPoint && isOneEntryPointAppBanner && (
                <button type="button" className="prisma-btn prisma-btn-circle od-btn-one-entry-iteration2" onClick={onFieldClick}>
                    <span glyph="arrow-right-tail" className={arrowIconClassName} style={{ fontSize: 'x-large' }} />
                </button>
            )}
            {!isCssVariationMobile && !!errorMessage && ErrorMessageComponent && (
                <ErrorMessageComponent errorMessage={errorMessage} dataTestId={id} />
            )}
            {!isCssVariationMobile && !!errorMessage && !ErrorMessageComponent && (
                <span className="odf-label odf-text-sm odf-space-outer-xs" data-testid="room-selector-children-error">
                    {errorMessage}
                </span>
            )}
        </div>
    );
};

Field.propTypes = {
    errorMessage: PropTypes.string,
    ErrorMessageComponent: PropTypes.func,
    icon: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired,
    placeholder: PropTypes.string,
    value: PropTypes.string.isRequired,
    isDisabled: PropTypes.bool,
};

Field.defaultProps = {
    errorMessage: '',
    placeholder: '',
    isDisabled: false,
};

export default Field;
